import React, {
  FunctionComponent,
  useEffect,
  useState,
} from "react";
import {
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";
import { Languages } from "constants/messages/languages";
import LanguageMenuItemStyled from "styles/components/LanguageMenuItemStyled";
import { FormattedMessage } from "react-intl";
import { Language as LanguageIcon } from "@mui/icons-material";
import { userService } from "services/user/UserService";
import { errorUtils } from "utils/api/errorUtils";
import { useAuthUser } from "hooks/useAuthUser";
import { useCookies } from "react-cookie";
import { HeaderCookie } from "components/molecules/header/Header";
import { languageUtils } from "utils/common/languageUtils";
import styles from "components/molecules/menu/LanguageDropdown/languageDropdown.module.scss"
import { DropdownStyled } from "components/atoms/select/DropdownStyled";
import { SideMenuItem } from "components/molecules/menu/SideMenu/SideMenuItem";
import { MenuItem } from "interfaces/menu/menu";

interface LanguageDropdownProps extends SelectProps {
  mobile: boolean;
  subMenuOpen: boolean;
  mobileMenuOpen?: boolean;
  onChange?: (event: SelectChangeEvent) => void;
  onOpen?: () => void;
  onCloseMobileMenu?: () => void;
  // selectedItem, selectedSubItem and onSelectMenuItem are mandatory for mobile menu, but optional for desktop
  selectedItem?: MenuItem;
  selectedSubItem?: MenuItem;
  onSelectMenuItem: (item: MenuItem) => void;
}
const languageOptions = () => { return Object.values(Languages).map(obj => ({ id: obj.id, name: obj.label })) };

export const LanguageDropdown: FunctionComponent<LanguageDropdownProps> = ({
  mobile,
  onCloseMobileMenu,
  onOpen,
  selectedItem,
  mobileMenuOpen,
  onSelectMenuItem,
  subMenuOpen,
}) => {
  const { user, cleanAndRefreshUserDetails } = useAuthUser();
  const [cookies, setCookies] = useCookies<string, HeaderCookie>(["lang"]);
  const [langSubMenuOpen, setLangSubMenuOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState("");

  const handleOpenSubMenu = (open?: boolean) => setLangSubMenuOpen((prevState) => open ? open : !prevState);
  const handleMenuClick = () => {
    onSelectMenuItem;
    handleOpenSubMenu();
  }

  const handleOnChange = (event?: SelectChangeEvent<unknown>, id?: string) => {
    if (event || id) {
      // depending on the menu (mobile or desktop), use either id or event target value to set the new language
      const newLanguage = id ?? event.target.value;
      setCurrentLanguage(newLanguage.toString());
      if (mobile) {
        onCloseMobileMenu();
        handleOpenSubMenu(false);
      }
      userService.updateSelfLanguage(newLanguage.toString()).then(() => {
        cleanAndRefreshUserDetails()
      }).catch(errorUtils.handleBackErrors)
    }
  }
  const setNewLangFromLocalStorage = () => {
    const newLanguage = languageUtils.getNavigatorLanguageFromUser(user, cookies.lang).toUpperCase();
    setCurrentLanguage(newLanguage);
  }

  useEffect(() => {
    if (user && !user?.language) {
      setNewLangFromLocalStorage();
      userService.updateSelfLanguage(currentLanguage).then(() => {
        setCookies("lang", currentLanguage, { path: "/", secure: false, sameSite: "lax" })
      }).catch(errorUtils.handleBackErrors)
    }
  }, [user]);

  useEffect(() => {
    if (!currentLanguage) {
      setNewLangFromLocalStorage();
    }
  }, []);

  const item: MenuItem = {
    href: undefined,
    Icon: undefined,
    label: "menu_language",
  };

  return (
    <>
      {mobile ?
        <div className={styles["language-select-mobile"]}>
          <SideMenuItem
            icon={<LanguageIcon fontSize="small" />}
            onSelectMenuItem={handleMenuClick}
            isLanguageItemSelected={!langSubMenuOpen}
            selectedItem={selectedItem}
            selectedSubItem={undefined}
            item={item}
            reduce={false}
            mobileMenuOpen={mobileMenuOpen}
            onCloseMobileMenu={onCloseMobileMenu}
            subMenuOpen={subMenuOpen}
          />

          {langSubMenuOpen &&
            <>
              {Object.values(Languages).map(lang =>
                <LanguageMenuItemStyled
                  sx={{ fontWeight: "bold" }}
                  key={lang.id} value={lang.id}
                  onClick={() => { handleOnChange(undefined, lang.id) }}
                  className={`${currentLanguage === lang.id ? styles.selected : ""}`}
                >
                  <FormattedMessage id={lang.label} />
                </LanguageMenuItemStyled>
              )}
            </>
          }
        </div>
        :
        // for Desktop
        <div className={styles["language-select-desktop"]}>
          <DropdownStyled selectedValue={currentLanguage} options={languageOptions()} onOpen={onOpen} onChange={handleOnChange} customClassName="language-dropwdown" menuId="language" />
        </div>
      }
    </>
  )
}
